import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Download, DollarSign, FileText, CheckCircle, XCircle, Calendar, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to parse DD/MM/YYYY date strings
const parseDateString = (dateStr) => {
  if (!dateStr) return null;
  if (dateStr.includes('T') || /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
    return new Date(dateStr);
  }
  const parts = dateStr.split('/');
  if (parts.length === 3) {
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const year = parseInt(parts[2], 10);
    return new Date(year, month, day);
  }
  return new Date(dateStr);
};

const AgencyStatements = () => {
  const [searchParams] = useSearchParams();
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [filterAgency, setFilterAgency] = useState('all');
  const [filterStatus, setFilterStatus] = useState(searchParams.get('filter') === 'unpaid' ? 'unpaid' : 'all');
  const [filterMonth, setFilterMonth] = useState('all');
  const [filterYear, setFilterYear] = useState('all');

  // Payment popup
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [paymentDate, setPaymentDate] = useState('');
  const [editableAmount, setEditableAmount] = useState('');
  const [editableInvoiceNumber, setEditableInvoiceNumber] = useState('');
  
  // Export state
  const [exportFormat, setExportFormat] = useState('excel');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [invoices, filterAgency, filterStatus, filterMonth, filterYear]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [invoicesRes, agenciesRes] = await Promise.all([
        axios.get(`${API}/invoices?include_historical=true`, { headers }),
        axios.get(`${API}/agencies`, { headers })
      ]);

      let allInvoices = invoicesRes.data;
      
      console.log('==== RAW API RESPONSE ====');
      console.log('Total invoices from API:', allInvoices.length);
      allInvoices.forEach(inv => {
        console.log(`  ${inv.invoice_number}: agency=${inv.agency_name}, agency_id=${inv.agency_id}, status=${inv.status}`);
      });
      
      // For agency users: Show ALL their invoices - no special filtering
      if (currentUser.role === 'agency' && currentUser.agency_id) {
        console.log('Current user agency_id:', currentUser.agency_id);
        
        // Filter by agency - show ALL invoices for this agency
        allInvoices = allInvoices.filter(inv => inv.agency_id === currentUser.agency_id);
        
        console.log('Agency invoices after agency filter:', allInvoices.length);
        allInvoices.forEach(inv => {
          console.log(`  Invoice: ${inv.invoice_number}, status: ${inv.status}, payment_status: ${inv.payment_status}, amount: ${inv.total_amount_with_vat || inv.total_amount}`);
        });
        
        // No further filtering - agency sees ALL their invoices
      }
      
      setInvoices(allInvoices);
      // Sort agencies alphabetically by name
      const sortedAgencies = agenciesRes.data.sort((a, b) => 
        (a.name || '').localeCompare(b.name || '')
      );
      setAgencies(sortedAgencies);
      setLoading(false);
    } catch (error) {
      toast.error("Failed to load data");
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...invoices];
    
    console.log('=== applyFilters START ===');
    console.log('currentUser.role:', currentUser.role);
    console.log('Total invoices before filters:', filtered.length);
    filtered.forEach(inv => console.log(`  ${inv.invoice_number}: status=${inv.status}`));
    
    // FOR AGENCIES: SHOW ALL INVOICES - NO FILTERING (check lowercase)
    if (currentUser.role && currentUser.role.toLowerCase() === 'agency') {
      console.log('AGENCY USER - SHOWING ALL INVOICES WITHOUT FILTERING');
      // Sort by invoice number - credit notes appear right after their sequence
      filtered.sort((a, b) => {
        const numA = a.invoice_number || '';
        const numB = b.invoice_number || '';
        const cleanA = numA.replace('NC', '');
        const cleanB = numB.replace('NC', '');
        if (cleanA === cleanB) {
          return numA.startsWith('NC') ? 1 : -1;
        }
        return cleanB.localeCompare(cleanA);
      });
      console.log('=== applyFilters END (agency) ===');
      console.log('Final filtered count:', filtered.length);
      filtered.forEach(inv => console.log(`  ${inv.invoice_number}: status=${inv.status}`));
      setFilteredInvoices(filtered);
      return;
    }
    
    console.log('filterStatus:', filterStatus);
    console.log('filterMonth:', filterMonth);
    console.log('filterYear:', filterYear);

    // For admin/accounting users, allow agency filter
    if (filterAgency !== 'all') {
      filtered = filtered.filter(inv => inv.agency_id === filterAgency);
    }

    // Filter by status
    if (filterStatus !== 'all') {
      filtered = filtered.filter(inv => {
        const isPaid = inv.payment_status === 'paid';
        return filterStatus === 'paid' ? isPaid : !isPaid;
      });
    }

    // Filter by month
    if (filterMonth !== 'all') {
      filtered = filtered.filter(inv => {
        const invDate = parseDateString(inv.invoice_date);
        return (invDate.getMonth() + 1) === parseInt(filterMonth);
      });
    }

    // Filter by year
    if (filterYear !== 'all') {
      filtered = filtered.filter(inv => {
        const invDate = parseDateString(inv.invoice_date);
        return invDate.getFullYear() === parseInt(filterYear);
      });
    }

    // Sort by invoice number (descending - newest first)
    // Credit notes (NCxxxxxx) should appear right after their sequence number
    filtered.sort((a, b) => {
      const numA = a.invoice_number || '';
      const numB = b.invoice_number || '';
      
      // Extract numeric part (remove NC prefix if present)
      const cleanA = numA.replace('NC', '');
      const cleanB = numB.replace('NC', '');
      
      // If same sequence number, credit note comes after regular invoice
      if (cleanA === cleanB) {
        // NC version comes after regular version (so NC shows below the related invoice)
        return numA.startsWith('NC') ? 1 : -1;
      }
      
      // Compare by sequence number descending
      return cleanB.localeCompare(cleanA);
    });
    
    console.log('=== applyFilters END ===');
    console.log('Final filtered count:', filtered.length);
    filtered.forEach(inv => console.log(`  ${inv.invoice_number}: status=${inv.status}`));

    setFilteredInvoices(filtered);
  };

  const calculateSummary = () => {
    const totalInvoices = filteredInvoices.length;
    const paidInvoices = filteredInvoices.filter(inv => inv.payment_status === 'paid');
    const unpaidInvoices = filteredInvoices.filter(inv => inv.payment_status !== 'paid');

    // Use total_amount_with_vat (official stored total)
    const totalPaid = paidInvoices.reduce((sum, inv) => sum + (inv.total_amount_with_vat || inv.total_amount || 0), 0);
    const totalUnpaid = unpaidInvoices.reduce((sum, inv) => sum + (inv.total_amount_with_vat || inv.total_amount || 0), 0);

    return { totalInvoices, totalPaid, totalUnpaid, paidCount: paidInvoices.length, unpaidCount: unpaidInvoices.length };
  };

  const getAvailableYears = () => {
    const years = new Set();
    invoices.forEach(inv => {
      const date = parseDateString(inv.invoice_date);
      if (date) years.add(date.getFullYear());
    });
    return Array.from(years).sort((a, b) => b - a);
  };

  const handleMarkAsPaid = async () => {
    if (!paymentDate) {
      toast.error("Please select a payment date");
      return;
    }

    // Validate amount - allow negative amounts for credit notes
    const amount = parseFloat(editableAmount);
    if (isNaN(amount)) {
      toast.error("Please enter a valid amount");
      return;
    }

    // Validate invoice number
    if (!editableInvoiceNumber || editableInvoiceNumber.trim() === '') {
      toast.error("Please enter an invoice number");
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/invoices/${selectedInvoice.id}/payment`,
        { 
          payment_status: 'paid', 
          payment_date: paymentDate,
          total_amount: amount,
          invoice_number: editableInvoiceNumber
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Invoice marked as paid and updated");
      setShowPaymentPopup(false);
      setSelectedInvoice(null);
      setPaymentDate('');
      setEditableAmount('');
      setEditableInvoiceNumber('');
      fetchData();
    } catch (error) {
      toast.error("Failed to update payment status");
    }
  };

  const handleMarkAsUnpaid = async () => {
    // Validate amount - allow negative amounts for credit notes
    const amount = parseFloat(editableAmount);
    if (isNaN(amount)) {
      toast.error("Please enter a valid amount");
      return;
    }

    // Validate invoice number
    if (!editableInvoiceNumber || editableInvoiceNumber.trim() === '') {
      toast.error("Please enter an invoice number");
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/invoices/${selectedInvoice.id}/payment`,
        { 
          payment_status: 'unpaid', 
          payment_date: null,
          total_amount: amount,
          invoice_number: editableInvoiceNumber
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Invoice marked as unpaid and updated");
      setShowPaymentPopup(false);
      setSelectedInvoice(null);
      setPaymentDate('');
      setEditableAmount('');
      setEditableInvoiceNumber('');
      fetchData();
    } catch (error) {
      toast.error("Failed to update payment status");
    }
  };

  const handleDownloadPDF = async (invoiceId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/invoices/${invoiceId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${invoiceId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Failed to download PDF");
    }
  };

  const handleDeleteInvoice = async (invoiceId, invoiceNumber) => {
    if (!window.confirm(`Are you sure you want to delete unpaid invoice ${invoiceNumber}? This action cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/invoices/${invoiceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Invoice ${invoiceNumber} deleted successfully`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete invoice");
    }
  };

  const handleExport = async (format) => {
    try {
      const token = localStorage.getItem('token');
      const agencyParam = filterAgency !== "all" ? `&agency_id=${filterAgency}` : "";
      const url = `${API}/invoices/export?format=${format}${agencyParam}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const filename = format === 'excel' 
        ? `agency_statements_${new Date().toISOString().split('T')[0]}.xlsx`
        : `agency_statements_${new Date().toISOString().split('T')[0]}.docx`;
      
      const blob = new Blob([response.data]);
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      
      toast.success(`Exported successfully as ${format === 'excel' ? 'Excel' : 'Word'} document`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to export");
    }
  };


  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    // If already in DD/MM/YYYY format, return as-is
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) return dateString;
    try {
      const date = parseDateString(dateString);
      if (!date || isNaN(date.getTime())) return dateString;
      return date.toLocaleDateString('en-GB');
    } catch {
      return dateString;
    }
  };

  const summary = calculateSummary();
  const availableYears = getAvailableYears();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: '#FFFFFF' }}>Agency Statements</h1>
          <p style={{ color: '#B0B0B0' }}>Track and manage invoice payments</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => handleExport('excel')} style={{ background: '#10B981', color: '#FFFFFF' }}>
            <Download className="mr-2 h-4 w-4" />Export Excel
          </Button>
          <Button onClick={() => handleExport('word')} style={{ background: '#3B82F6', color: '#FFFFFF' }}>
            <Download className="mr-2 h-4 w-4" />Export Word
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card style={{ background: 'rgba(30, 42, 74, 0.6)', border: '1px solid rgba(74, 90, 138, 0.3)' }}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium" style={{ color: '#B0B0B0' }}>Total Invoices</CardTitle>
            <FileText className="h-4 w-4" style={{ color: '#FFFFFF' }} />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" style={{ color: '#FFFFFF' }}>{summary.totalInvoices}</div>
            <p className="text-xs" style={{ color: '#B0B0B0' }}>
              {summary.paidCount} paid, {summary.unpaidCount} unpaid
            </p>
          </CardContent>
        </Card>

        <Card style={{ background: 'rgba(30, 42, 74, 0.6)', border: '1px solid rgba(74, 90, 138, 0.3)' }}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium" style={{ color: '#B0B0B0' }}>Total Paid</CardTitle>
            <CheckCircle className="h-4 w-4" style={{ color: '#4ADE80' }} />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" style={{ color: '#4ADE80' }}>€{summary.totalPaid.toFixed(2)}</div>
            <p className="text-xs" style={{ color: '#B0B0B0' }}>
              {summary.paidCount} invoice{summary.paidCount !== 1 ? 's' : ''}
            </p>
          </CardContent>
        </Card>

        <Card style={{ background: 'rgba(30, 42, 74, 0.6)', border: '1px solid rgba(74, 90, 138, 0.3)' }}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium" style={{ color: '#B0B0B0' }}>Total Unpaid</CardTitle>
            <XCircle className="h-4 w-4" style={{ color: '#FF6B6B' }} />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" style={{ color: '#FF6B6B' }}>€{summary.totalUnpaid.toFixed(2)}</div>
            <p className="text-xs" style={{ color: '#B0B0B0' }}>
              {summary.unpaidCount} invoice{summary.unpaidCount !== 1 ? 's' : ''}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card style={{ background: 'rgba(30, 42, 74, 0.6)', border: '1px solid rgba(74, 90, 138, 0.3)' }}>
        <CardContent className="pt-4">
          <div className={`grid grid-cols-1 md:grid-cols-2 ${currentUser.role !== 'agency' ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-4`}>
            {/* Agency Filter - Hidden for agency users */}
            {currentUser.role !== 'agency' && (
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#E8E8E8' }}>Agency</label>
                <Select value={filterAgency} onValueChange={setFilterAgency}>
                  <SelectTrigger style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                    <SelectValue placeholder="All Agencies" />
                  </SelectTrigger>
                  <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                    <SelectItem value="all">All Agencies</SelectItem>
                    {agencies.filter(a => a.is_active !== false).map(agency => (
                      <SelectItem key={agency.id} value={agency.id}>
                        {agency.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#E8E8E8' }}>Payment Status</label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="unpaid">Unpaid</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Month Filter */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#E8E8E8' }}>Month</label>
              <Select value={filterMonth} onValueChange={setFilterMonth}>
                <SelectTrigger style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                  <SelectValue placeholder="All Months" />
                </SelectTrigger>
                <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                  <SelectItem value="all">All Months</SelectItem>
                  <SelectItem value="1">January</SelectItem>
                  <SelectItem value="2">February</SelectItem>
                  <SelectItem value="3">March</SelectItem>
                  <SelectItem value="4">April</SelectItem>
                  <SelectItem value="5">May</SelectItem>
                  <SelectItem value="6">June</SelectItem>
                  <SelectItem value="7">July</SelectItem>
                  <SelectItem value="8">August</SelectItem>
                  <SelectItem value="9">September</SelectItem>
                  <SelectItem value="10">October</SelectItem>
                  <SelectItem value="11">November</SelectItem>
                  <SelectItem value="12">December</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Year Filter */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#E8E8E8' }}>Year</label>
              <Select value={filterYear} onValueChange={setFilterYear}>
                <SelectTrigger style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                  <SelectValue placeholder="All Years" />
                </SelectTrigger>
                <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                  <SelectItem value="all">All Years</SelectItem>
                  {availableYears.map(year => (
                    <SelectItem key={year} value={year.toString()}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="mt-3 text-sm" style={{ color: '#FFD700' }}>
            Showing {filteredInvoices.length} of {invoices.length} invoices
          </div>
        </CardContent>
      </Card>

      {/* Invoices Table */}
      <Card className="card-hover" style={{ background: 'rgba(30, 42, 74, 0.6)', border: '1px solid rgba(74, 90, 138, 0.3)' }}>
        <CardHeader>
          <CardTitle style={{ color: '#FFFFFF' }}>Invoices ({filteredInvoices.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(74, 90, 138, 0.3)' }}>
                  <th className="text-left p-3 font-semibold" style={{ color: '#B0B0B0' }}>Invoice Date</th>
                  <th className="text-left p-3 font-semibold" style={{ color: '#B0B0B0' }}>Invoice Number</th>
                  <th className="text-left p-3 font-semibold" style={{ color: '#B0B0B0' }}>Agency Name</th>
                  <th className="text-right p-3 font-semibold" style={{ color: '#B0B0B0' }}>Total (VAT incl.)</th>
                  <th className="text-center p-3 font-semibold" style={{ color: '#B0B0B0' }}>Status</th>
                  <th className="text-center p-3 font-semibold" style={{ color: '#B0B0B0' }}>Payment Date</th>
                  {(currentUser.role === 'admin' || currentUser.role === 'accounting') && (
                    <th className="text-center p-3 font-semibold" style={{ color: '#B0B0B0' }}>Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.length === 0 ? (
                  <tr>
                    <td colSpan={(currentUser.role === 'admin' || currentUser.role === 'accounting') ? "7" : "6"} className="text-center p-8" style={{ color: '#B0B0B0' }}>
                      No invoices found
                    </td>
                  </tr>
                ) : (
                  filteredInvoices.map((invoice) => (
                    <tr 
                      key={invoice.id} 
                      style={{ borderBottom: '1px solid rgba(74, 90, 138, 0.3)' }}
                      className="transition-colors hover:bg-opacity-80"
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <td className="p-3" style={{ color: '#E8E8E8' }}>{formatDate(invoice.invoice_date)}</td>
                      <td className="p-3">
                        <button
                          onClick={() => handleDownloadPDF(invoice.id)}
                          className="font-semibold flex items-center gap-1 transition-colors"
                          style={{ color: '#60A5FA' }}
                          onMouseEnter={(e) => e.currentTarget.style.color = '#93C5FD'}
                          onMouseLeave={(e) => e.currentTarget.style.color = '#60A5FA'}
                        >
                          {invoice.invoice_number}
                          <Download className="h-3 w-3" />
                        </button>
                      </td>
                      <td className="p-3" style={{ color: '#E8E8E8', fontWeight: '500' }}>
                        {invoice.agency_name || 'N/A'}
                      </td>
                      <td className="p-3 text-right">
                        {currentUser.role === 'admin' || currentUser.role === 'accounting' ? (
                          <button
                            onClick={() => {
                              setSelectedInvoice(invoice);
                              setPaymentDate(invoice.payment_date || '');
                              setEditableAmount((invoice.total_amount_with_vat || invoice.total_amount || 0).toFixed(2));
                              setEditableInvoiceNumber(invoice.invoice_number || '');
                              setShowPaymentPopup(true);
                            }}
                            className="font-bold transition-all px-2 py-1 rounded"
                            style={{ color: '#FFFFFF' }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            €{(invoice.total_amount_with_vat || invoice.total_amount || 0).toFixed(2)}
                          </button>
                        ) : (
                          <span className="font-bold" style={{ color: '#FFFFFF' }}>
                            €{(invoice.total_amount_with_vat || invoice.total_amount || 0).toFixed(2)}
                          </span>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        {invoice.payment_status === 'paid' ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: 'rgba(74, 222, 128, 0.2)', color: '#4ADE80' }}>
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Paid
                          </span>
                        ) : invoice.payment_status === 'pending' ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: 'rgba(251, 191, 36, 0.2)', color: '#FBB024' }}>
                            <XCircle className="h-3 w-3 mr-1" />
                            Agency Marked as Paid
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: 'rgba(255, 107, 107, 0.2)', color: '#FF6B6B' }}>
                            <XCircle className="h-3 w-3 mr-1" />
                            Unpaid
                          </span>
                        )}
                      </td>
                      <td className="p-3 text-center" style={{ color: '#B0B0B0' }}>
                        {invoice.payment_date ? formatDate(invoice.payment_date) : '-'}
                      </td>
                      {(currentUser.role === 'admin' || currentUser.role === 'accounting') && (
                        <td className="p-3 text-center">
                          {invoice.payment_status === 'unpaid' && (
                            <button
                              onClick={() => handleDeleteInvoice(invoice.id, invoice.invoice_number)}
                              className="transition-colors p-2 rounded hover:bg-red-500/10"
                              style={{ color: '#FF6B6B' }}
                              title="Delete unpaid invoice"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Payment Popup */}
      {showPaymentPopup && selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }} className="rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto border">
            <h2 className="text-xl font-bold mb-4" style={{ color: '#FFFFFF' }}>Update Invoice Details</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: '#E8E8E8' }}>Invoice Number *</label>
                <Input 
                  value={editableInvoiceNumber} 
                  onChange={(e) => setEditableInvoiceNumber(e.target.value)}
                  style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#FFFFFF' }}
                  className="font-medium"
                  placeholder="Enter invoice number"
                />
                <p className="text-xs mt-1" style={{ color: '#FBBF24' }}>
                  💡 Edit if invoice number is incorrect
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: '#E8E8E8' }}>Agency</label>
                <Input 
                  value={selectedInvoice.agency_name || 'N/A'} 
                  disabled 
                  style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: '#E8E8E8' }}>Amount (€) *</label>
                <Input 
                  type="number"
                  step="0.01"
                  value={editableAmount} 
                  onChange={(e) => setEditableAmount(e.target.value)}
                  style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#FFFFFF' }}
                  className="font-bold text-lg"
                  placeholder="0.00"
                />
                <p className="text-xs mt-1" style={{ color: '#FBBF24' }}>
                  💡 Edit amount if incorrect
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: '#E8E8E8' }}>Payment Date (optional for unpaid)</label>
                <Input
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: '#E8E8E8' }}>Current Status</label>
                <div className="text-sm p-3 rounded-md" style={{ background: '#0A1128', border: '1px solid #4A5A8A' }}>
                  {selectedInvoice.payment_status === 'paid' ? (
                    <span className="font-semibold flex items-center" style={{ color: '#4ADE80' }}>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Already Paid
                    </span>
                  ) : selectedInvoice.payment_status === 'pending' ? (
                    <span className="font-semibold flex items-center" style={{ color: '#FBB024' }}>
                      <XCircle className="h-4 w-4 mr-2" />
                      Agency Marked as Paid
                    </span>
                  ) : (
                    <span className="font-semibold flex items-center" style={{ color: '#FF6B6B' }}>
                      <XCircle className="h-4 w-4 mr-2" />
                      Unpaid
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 mt-6">
              <Button
                onClick={handleMarkAsPaid}
                style={{ background: '#4ADE80', color: '#0A1128' }}
                className="flex-1 hover:opacity-90 font-semibold text-sm"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Save & Mark as Paid
              </Button>
              <Button
                onClick={handleMarkAsUnpaid}
                style={{ background: '#FF6B6B', color: '#FFFFFF' }}
                className="flex-1 hover:opacity-90 font-semibold text-sm"
              >
                <XCircle className="h-4 w-4 mr-2" />
                Save & Mark as Unpaid
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowPaymentPopup(false);
                  setSelectedInvoice(null);
                  setPaymentDate('');
                  setEditableAmount('');
                  setEditableInvoiceNumber('');
                }}
                style={{ borderColor: '#4A5A8A', color: '#B0B0B0' }}
                className="flex-1 font-semibold text-sm"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgencyStatements;
